import React, { useState } from "react";
import { toast } from "sonner";
import { Save, Phone, Mail, MessageCircle, Megaphone, ToggleLeft } from "lucide-react";
import { useData } from "../../context/DataContext";

const SiteSettings = () => {
    const { settings, updateSettings } = useData();
    const [form, setForm] = useState(settings);

    const save = (e) => {
        e.preventDefault();
        updateSettings(form);
        toast.success("Settings saved. Site reflects changes immediately.");
    };

    return (
        <div data-testid="admin-settings-page">
            <h1 className="font-display text-3xl text-ink">Site settings</h1>
            <p className="text-ink-light text-sm mt-1">Update contact details, banners and global toggles.</p>

            <form onSubmit={save} className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
                    <h2 className="font-semibold text-ink flex items-center gap-2"><Phone className="w-4 h-4 text-saffron-600" /> Contact details</h2>
                    <Field label="Phone" value={form.phone} icon={Phone} onChange={(v) => setForm({ ...form, phone: v })} />
                    <Field label="Email" value={form.email} icon={Mail} onChange={(v) => setForm({ ...form, email: v })} />
                    <Field label="WhatsApp number (with country code)" value={form.whatsapp} icon={MessageCircle} onChange={(v) => setForm({ ...form, whatsapp: v })} />
                </div>

                <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
                    <h2 className="font-semibold text-ink flex items-center gap-2"><Megaphone className="w-4 h-4 text-teal-600" /> Public messaging</h2>
                    <Field label="Promo banner text (homepage)" value={form.promoText} onChange={(v) => setForm({ ...form, promoText: v })} />
                    <Field label="Hero tagline / banner message" value={form.bannerMessage} onChange={(v) => setForm({ ...form, bannerMessage: v })} />
                </div>

                <div className="bg-white border border-slate-200 rounded-xl p-6 lg:col-span-2 flex items-center justify-between gap-4 flex-wrap">
                    <div>
                        <h2 className="font-semibold text-ink flex items-center gap-2"><ToggleLeft className="w-4 h-4 text-saffron-600" /> Bookings availability</h2>
                        <p className="text-xs text-ink-light mt-1">Temporarily disable Book Now buttons site-wide.</p>
                    </div>
                    <label className="flex items-center gap-3 cursor-pointer">
                        <span className={`text-sm font-semibold ${form.bookingsEnabled ? "text-teal-600" : "text-red-600"}`}>
                            {form.bookingsEnabled ? "Enabled" : "Disabled"}
                        </span>
                        <span className="relative inline-flex h-6 w-11 items-center rounded-full transition" style={{ background: form.bookingsEnabled ? "#00897B" : "#94a3b8" }}>
                            <input type="checkbox" checked={form.bookingsEnabled} onChange={(e) => setForm({ ...form, bookingsEnabled: e.target.checked })} className="sr-only" data-testid="settings-bookings-toggle" />
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${form.bookingsEnabled ? "translate-x-6" : "translate-x-1"}`} />
                        </span>
                    </label>
                </div>

                <div className="lg:col-span-2 flex justify-end">
                    <button type="submit" className="px-6 py-3 rounded-xl bg-saffron-500 hover:bg-saffron-600 text-white font-semibold flex items-center gap-2 transition shadow-md" data-testid="settings-save">
                        <Save className="w-4 h-4" /> Save changes
                    </button>
                </div>
            </form>
        </div>
    );
};

const Field = ({ label, icon: Icon, value, onChange }) => (
    <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">{label}</label>
        <div className="relative mt-1">
            {Icon && <Icon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-light" />}
            <input
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                className={`w-full ${Icon ? "pl-9" : "pl-3"} pr-3 py-2.5 rounded-lg border border-slate-200 bg-white`}
            />
        </div>
    </div>
);

export default SiteSettings;
